import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Box, Flex, Spinner } from "theme-ui";
import moment from "moment-timezone";

import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import SelectInput from "components/forms/SelectInput";
import SearchField from "components/forms/SearchField";
import BasicButton from "components/forms/BasicButton";
import { themevals } from "theme/themevals";
import { mnt, mntType } from "theme/maintenanceTokens";
import {
  SourcePill,
  UnresolvedPill,
  UrgentPill,
  MapStatusPill,
  showsMapStatus,
  lifecycleBadge,
  slaCell,
  receivedCell,
  formatNte,
  canMatchWo,
  canGenerateRfqForWo,
  unresolvedReason as woUnresolvedReason,
} from "./workOrderPresentation";

import { getWorkOrders } from "api/ServicePlans";
import { resolveWoNextAction, woPriorityBand, woLastMovedAt } from "./workOrderNextAction";

import MatchCandidatesModal from "./MatchCandidatesModal";
import PlanSectionHeader from "./PlanSectionHeader";

/**
 * PLAN_DETAIL_INTERACTION_MODEL.md / Session 48 — the plan-scoped Work
 * Order list embedded directly in Plan Detail.
 *
 * The conceptual fix: the plan is the work surface, the global WO Queue
 * is cross-plan triage you shouldn't have to visit. Until now Plan
 * Detail could CREATE work orders (header + row actions) but gave no
 * way to SEE, open, or act on the ones that already exist against the
 * plan — you had to leave for /maintenance/work-orders. This section
 * closes that: every WO on the plan, triaged in place, with the same
 * lifecycle actions (Match / Generate RFQ) the queue has, and each
 * WO# linking into the full WO detail page.
 *
 * OPERATING_MODEL §6.5 (Session 52): a WO is never "rejected" — we
 * don't reject the client's own instruction. The reject affordance was
 * removed here. Declining work happens through the trunk RFQ/Quote
 * flow, not on the WO. Any legacy `rejected` rows still render their
 * badge, but no reject action is offered. Also §6.5: a WO whose RFQ
 * has been generated (status='converted') is an IN-PROGRESS case, not
 * an archived one — labels and the lifecycle filter say "In progress",
 * and only legacy `rejected` rows are treated as closed.
 *
 * Work-order-fed only — quote-fed plans have no work orders, so the
 * section renders nothing (same gate as the Batches section).
 *
 * Reads GET /maintenance/work-orders?servicePlanId=… (existing list
 * endpoint, already plan-filterable). No new backend.
 *
 * SESSION 55: the row helpers that used to be duplicated here (badge,
 * SLA, aging, NTE) now come from `./workOrderPresentation`, shared with
 * the queue and the WO case file. That was the "later no-behavior
 * cleanup" flagged in PROGRESS Session 48; it is done, and the three
 * copies had in fact drifted before it landed. The row also now carries
 * the source chip and the amber unresolved pill, matching the queue.
 *
 * NOTE: this section is slated for the P-b rebuild (a dense, filterable
 * work queue with status rollups, age and quick actions — see
 * WHATS_LEFT.md). The shared layer is deliberately the piece to build
 * that on.
 */

const LIFECYCLE_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "actionable", label: "Needs action" },
  { value: "needs_match", label: "Needs match" },
  { value: "needs_convert", label: "Needs RFQ" },
  { value: "in_progress", label: "In progress" },
  { value: "", label: "All" },
];

export default function PlanWorkOrdersSection({
  planId,
  intakeStyle,
  canEdit,
  onChanged,
  workOrders: workOrdersProp,
  allWorkOrders,
  scopeGroupId,
  loading: loadingProp,
  lifecycleFilter: lifecycleFilterProp,
  onLifecycleChange,
  onReload,
}: {
  planId: string;
  intakeStyle?: "work_order_fed" | "quote_fed";
  /** Admin-only lifecycle actions (Match / Generate RFQ). Client
   * viewers see the read-only list. */
  canEdit: boolean;
  /** Bubbled up so Plan Detail can refresh stats / activity / the sites
   * table after a WO changes state here. */
  onChanged?: () => void;
  /** When the parent owns the plan's WO list (so the Locations table can
   * show per-row WOs from the same source), it passes the list + filter
   * state + a reload fn down and this section becomes a pure view. When
   * omitted, the section self-fetches (standalone use). */
  workOrders?: any[] | null;
  /** EVERY WO on the plan, unfiltered. The rollup strip has to count
   * the whole plan, not the current lifecycle slice, or the numbers
   * change as you filter by them. */
  allWorkOrders?: any[] | null;
  /** P-i/P-l — the page's shared batch scope. Narrows both the rows and
   * the rollup, so the section agrees with the rest of the page rather
   * than quietly showing plan-wide numbers under a batch heading. */
  scopeGroupId?: string | null;
  loading?: boolean;
  lifecycleFilter?: string;
  onLifecycleChange?: (v: string) => void;
  onReload?: () => void;
}) {
  const navigate = useNavigate();

  const parentOwned = workOrdersProp !== undefined;

  const [selfWorkOrders, setSelfWorkOrders] = useState<any[] | null>(null);
  const [selfLoading, setSelfLoading] = useState(true);
  const [selfLifecycle, setSelfLifecycle] = useState<string>("open");
  // P-b — the rollup's stage chips filter on the DERIVED stage, a
  // different axis from the lifecycle dropdown. Kept as its own state so
  // picking "quoted" does not silently rewrite the lifecycle selection
  // to something that means more than the user asked for.
  const [stageFilter, setStageFilter] = useState<string>("");
  const [collapsed, setCollapsed] = useState(false);

  const [matchTarget, setMatchTarget] = useState<any>(null);
  /**
   * The open work order, and the two densities that hang off it
   * (Session 82). Same mechanism as the location roster: null gives the
   * list the full width and its rows earn extra columns; set collapses
   * the list to 320px and opens the record beside it.
   */
  const [openWoId, setOpenWoId] = useState<string | null>(null);
  // Pagination for the active WO list. At 48+ open WOs the full list forces
  // a long scroll and pushes everything below it down; the operator picks a
  // page size (or "all"). pageSize=0 means show all.
  const [pageSize, setPageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  // Search over the already-loaded plan WO list. Mirrors the per-page
  // search box on Quotes/Invoices (same SearchField component, same
  // placement above the table), but filters CLIENT-SIDE: the plan fetches
  // its work orders once and unfiltered, so a keystroke costs no request.
  // The 3-character minimum and 500ms debounce TableData applies exist
  // only to spare the server a round trip per keystroke, so they are not
  // carried over here - filtering is instant from the first character.
  const [search, setSearch] = useState<string>("");
  // Row order. "priority" is the default because the server hands this
  // list back `receivedAt DESC` and nothing re-sorts it — which buries
  // every moving case under whatever intake landed most recently.
  // "received" keeps the old behaviour one click away.
  const [sortMode, setSortMode] = useState<"priority" | "received">("priority");

  const isWorkOrderFed = intakeStyle === "work_order_fed";

  const workOrders = parentOwned ? workOrdersProp ?? [] : selfWorkOrders;
  const loading = parentOwned ? !!loadingProp : selfLoading;
  const lifecycleFilter = parentOwned ? lifecycleFilterProp ?? "open" : selfLifecycle;

  // Page scope first, then the stage chip. Both are client-side over an
  // already-loaded plan list (tens of rows).
  const inScope = React.useCallback(
    (rows: any[]) =>
      scopeGroupId
        ? rows.filter(
            (wo: any) => (wo?.group?.id ?? wo?.groupId ?? null) === scopeGroupId,
          )
        : rows,
    [scopeGroupId],
  );

  // Stage narrowing happens client-side on the already-loaded plan list.
  // A plan's WO list is tens of rows and already in hand, so a chip
  // click costs nothing; pushing it to the server would mean a new query
  // param threaded through the parent for no gain at this size.
  const visibleWorkOrders = React.useMemo(() => {
    let rows = inScope(workOrders ?? []);
    // SCALE: the plan now fetches its work orders ONCE, unfiltered, and
    // every narrowing happens here. The lifecycle buckets mirror the
    // server's (see WorkOrderService.list) so switching the dropdown
    // costs no request.
    rows = rows.filter((wo: any) => matchesLifecycle(wo, lifecycleFilter));
    if (!stageFilter) return rows;
    // Chips are NEXT-STEP buckets, not stages: "to post to Corrigo" is
    // the thing an operator batches through, and it is a different set
    // from "quote_sent". Filter on the resolved action so the chip and
    // the rows below it always describe the same work.
    const wanted = stageFilter.split(",");
    rows = rows.filter((wo: any) =>
      wanted.includes(resolveWoNextAction(wo, { intakeStyle }).kind),
    );
    return rows;
  }, [workOrders, stageFilter, inScope, lifecycleFilter, intakeStyle]);

  // Search is applied LAST, as its own memo, so typing does not re-run the
  // lifecycle/stage narrowing (and so the rollup chips above keep counting
  // the unsearched set - a search is a way to find a row, not a change to
  // what work exists).
  const searchedWorkOrders = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return visibleWorkOrders;
    return visibleWorkOrders.filter((wo: any) => matchesSearch(wo, q));
  }, [visibleWorkOrders, search]);

  // Order LAST, after every narrowing, so the rollup counts and the
  // search set are unaffected by it. Band first (see woPriorityBand),
  // then most-recent movement inside the band: recency surfaces without
  // being allowed to outrank lateness.
  const sortedWorkOrders = React.useMemo(() => {
    const rows = [...searchedWorkOrders];
    if (sortMode === "received") {
      return rows.sort(
        (a, b) =>
          new Date(b?.receivedAt ?? 0).getTime() -
          new Date(a?.receivedAt ?? 0).getTime(),
      );
    }
    return rows.sort((a, b) => {
      const ba = woPriorityBand(a, resolveWoNextAction(a, { intakeStyle }));
      const bb = woPriorityBand(b, resolveWoNextAction(b, { intakeStyle }));
      if (ba !== bb) return ba - bb;
      return woLastMovedAt(b) - woLastMovedAt(a);
    });
  }, [searchedWorkOrders, sortMode, intakeStyle]);

  // Reset to the first page whenever the visible set changes underneath us
  // (filter switch, batch scope, search, reload) so we're never stranded on
  // an empty trailing page.
  const visibleCount = searchedWorkOrders.length;
  React.useEffect(() => {
    setPage(1);
  }, [lifecycleFilter, stageFilter, pageSize, search, visibleCount]);

  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(visibleCount / pageSize)) : 1;
  const safePage = Math.min(page, totalPages);
  const pagedWorkOrders = React.useMemo(() => {
    if (pageSize <= 0) return sortedWorkOrders;
    const start = (safePage - 1) * pageSize;
    return sortedWorkOrders.slice(start, start + pageSize);
  }, [sortedWorkOrders, pageSize, safePage]);

  // Derived AFTER the page is computed, not beside the state:
  // `pagedWorkOrders` is a const declared below, so reading it earlier
  // is a temporal-dead-zone error rather than an undefined.
  const openWo = pagedWorkOrders.find((w: any) => w.id === openWoId) ?? null;

  // A record that has left the page cannot stay open behind it — the
  // pane would otherwise describe a row the list no longer shows.
  useEffect(() => {
    if (openWoId && !pagedWorkOrders.some((w: any) => w.id === openWoId)) setOpenWoId(null);
  }, [pagedWorkOrders, openWoId]);

  const selfLoad = useCallback(async () => {
    setSelfLoading(true);
    const query: any = { servicePlanId: planId };
    if (selfLifecycle) query.lifecycle = selfLifecycle;
    const res = await getWorkOrders({ query });
    setSelfWorkOrders(Array.isArray(res) ? res : []);
    setSelfLoading(false);
  }, [planId, selfLifecycle]);
  useEffect(() => {
    if (!parentOwned && isWorkOrderFed) selfLoad();
  }, [selfLoad, isWorkOrderFed, parentOwned]);

  const reload = useCallback(() => {
    if (parentOwned) {
      if (onReload) onReload();
    } else {
      selfLoad();
    }
  }, [parentOwned, onReload, selfLoad]);

  const setLifecycleFilter = (v: string) => {
    if (parentOwned) {
      if (onLifecycleChange) onLifecycleChange(v);
    } else {
      setSelfLifecycle(v);
    }
  };

  // Count for the header.
  const count = searchedWorkOrders.length;

  // Quote-fed plans have no work orders — render nothing at all.
  if (!isWorkOrderFed) return null;

  return (
    <>
      <PlanSectionHeader
        label="Active Work Orders"
        hint={
          loading
            ? "loading"
            : `${count} ${
                lifecycleFilter === "open"
                  ? "open"
                  : lifecycleFilter === "actionable"
                  ? "need action"
                  : "shown"
              }`
        }
        action={
          <Flex sx={{ alignItems: "center", gap: "10px" }}>
            <Box sx={{ minWidth: "180px" }}>
              <SelectInput
                name="planWoLifecycle"
                value={lifecycleFilter}
                options={LIFECYCLE_OPTIONS}
                onChange={(v: any) => setLifecycleFilter(v ?? "open")}
                searchable={false}
                clearable={false}
              />
            </Box>
            <Box
              as="button"
              onClick={() => setCollapsed((v) => !v)}
              sx={{
                background: "transparent",
                border: "none",
                padding: 0,
                fontSize: "12px",
                fontFamily: "Poppins",
                color: mnt.link,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {collapsed ? "Show" : "Hide"}
            </Box>
          </Flex>
        }
      />

      {!collapsed && (
        <Box sx={{ marginBottom: "24px" }}>
          {/* P-b rollup strip (mockup 26). The daily question on a
           * WO-fed plan is "what is waiting on me", and the answer has
           * to be visible before you read a single row. Counts come
           * from the UNFILTERED plan set so they do not move as you
           * filter by them, and each chip is the filter it describes. */}
          <WorkQueueRollup
            workOrders={inScope(allWorkOrders ?? workOrders ?? [])}
            intakeStyle={intakeStyle}
            activeLifecycle={lifecycleFilter}
            activeStage={stageFilter}
            onPickLifecycle={(v) => {
              setStageFilter("");
              setLifecycleFilter(v);
            }}
            onPickStage={(v) => {
              // Stage chips need the whole plan in view, not the
              // "actionable" slice, or a quoted WO is filtered out by
              // the lifecycle before the stage filter ever sees it.
              if (lifecycleFilter !== "") setLifecycleFilter("");
              setStageFilter((prev) => (prev === v ? "" : v));
            }}
          />
          {/* Search box, mirroring the Quotes/Invoices toolbar: same
            * SearchField, sitting above the table. Placed BELOW the rollup
            * so the chips keep reading as plan-wide counts and the search
            * reads as a way to find a row inside whatever they select. */}
          <Flex
            sx={{
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "10px",
              marginBottom: "10px",
            }}
          >
            {!!search.trim() && (
              <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
                {count} of {visibleWorkOrders.length} shown
              </Subtext>
            )}
            <SearchField
              name="planWoSearch"
              value={search}
              width={"250px"}
              onChange={(e: any) => setSearch(e?.target?.value ?? "")}
            />
          </Flex>
          {loading ? (
            <Box
              sx={{
                background: mnt.cardBg,
                border: "0.5px solid",
                borderColor: mnt.divider,
                borderRadius: "8px",
                padding: "30px",
                textAlign: "center",
              }}
            >
              <Spinner />
            </Box>
          ) : count === 0 ? (
            <Box
              sx={{
                background: mnt.cardBg,
                border: "1px dashed",
                borderColor: mnt.divider,
                borderRadius: "8px",
                padding: "18px 20px",
                textAlign: "center",
              }}
            >
              <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
                {search.trim()
                  ? "No work orders match this search. Clear the search box to see the rest of this filter."
                  : lifecycleFilter === "open"
                  ? "No open work orders on this plan right now. Raise one with \u201c+ New Work Order\u201d above, or switch the filter to see finished cases."
                  : lifecycleFilter === "actionable"
                  ? "No work orders need attention on this plan right now. Raise one with \u201c+ New Work Order\u201d above, or switch the filter to see in-progress cases."
                  : "No work orders match this filter."}
              </Subtext>
            </Box>
          ) : (
            <>
            <Flex sx={{ gap: "16px", alignItems: "flex-start" }}>
            <Box
              sx={{
                background: mnt.cardBg,
                border: "0.5px solid",
                borderColor: mnt.divider,
                borderRadius: "8px",
                overflow: "hidden",
                // The one line that drives both densities.
                flex: openWo ? "0 0 320px" : "1 1 auto",
                minWidth: 0,
              }}
            >
              {/* The column header only makes sense in the wide state;
                  narrow, there are no columns left for it to label. */}
              {!openWo && <PlanWoListHeader />}
              {pagedWorkOrders.map((wo, idx) => (
                <PlanWoListRow
                  key={wo.id}
                  wo={wo}
                  isLast={idx === pagedWorkOrders.length - 1}
                  dense={!!openWo}
                  isOpen={wo.id === openWoId}
                  onOpen={() => setOpenWoId((cur) => (cur === wo.id ? null : wo.id))}
                />
              ))}
            </Box>

            {openWo && (
              <PlanWoPane
                wo={openWo}
                canEdit={canEdit}
                onClose={() => setOpenWoId(null)}
                onOpen={() => navigate(`/maintenance/work-orders/${openWo.id}`)}
                onMatch={() => setMatchTarget(openWo)}
              />
            )}
            </Flex>
            {/* Pagination bar — page-size selector (5/10/15/20/all) plus
             * prev/next. Client-side over the already-fetched set. Shown
             * whenever there's more than one page, or always so the size
             * control is reachable. */}
            <Flex
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                marginTop: "10px",
                flexWrap: "wrap",
              }}
            >
              <Flex
                sx={{
                  alignItems: "center",
                  gap: "6px",
                  // `whiteSpace: nowrap` on the ROW, not on the one label
                  // that showed the problem. Without it these labels are
                  // shrinkable flex items, so when the row runs out of
                  // width the browser breaks them at every space: "1-10
                  // of 48" rendered as four stacked lines. "Show" and
                  // "Sort" sit in the same row and would do the same
                  // thing at a narrower width.
                  //
                  // `flexWrap` then makes the row wrap BETWEEN controls
                  // instead, which is the behaviour that was intended
                  // when it ran out of room.
                  whiteSpace: "nowrap",
                  flexWrap: "wrap",
                }}
              >
                <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
                  Show
                </Subtext>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  style={{
                    fontFamily: "Poppins",
                    fontSize: "12px",
                    padding: "4px 8px",
                    borderRadius: "6px",
                    border: `1px solid ${mnt.divider}`,
                    background: mnt.cardBg,
                    color: mnt.ink,
                    cursor: "pointer",
                  }}
                >
                  {[5, 10, 15, 20].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                  <option value={0}>All</option>
                </select>
                <Box
                  as="div"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "11px",
                    color: mnt.textMuted,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {pageSize > 0
                    ? `${(safePage - 1) * pageSize + 1}\u2013${Math.min(
                        safePage * pageSize,
                        visibleCount,
                      )} of ${visibleCount}`
                    : `all ${visibleCount}`}
                </Box>

                <Subtext
                  fontSize={"11px"}
                  fontFamily={"Poppins"}
                  margin={0}
                  color={mnt.textMuted}
                >
                  Sort
                </Subtext>
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value as "priority" | "received")}
                  title="Priority puts late and in-flight cases first. Newest intake is the raw arrival order."
                  style={{
                    fontFamily: "Poppins",
                    fontSize: "12px",
                    padding: "4px 8px",
                    borderRadius: "6px",
                    border: `1px solid ${mnt.divider}`,
                    background: mnt.cardBg,
                    color: mnt.ink,
                    cursor: "pointer",
                  }}
                >
                  <option value="priority">Priority</option>
                  <option value="received">Newest intake</option>
                </select>
              </Flex>

              {pageSize > 0 && totalPages > 1 && (
                <Flex sx={{ alignItems: "center", gap: "6px" }}>
                  <Box
                    as="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      padding: "4px 10px",
                      borderRadius: "6px",
                      border: `1px solid ${mnt.divider}`,
                      background: mnt.cardBg,
                      color: safePage <= 1 ? mnt.textMuted : mnt.ink,
                      cursor: safePage <= 1 ? "default" : "pointer",
                    }}
                  >
                    Prev
                  </Box>
                  <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
                    Page {safePage} of {totalPages}
                  </Subtext>
                  <Box
                    as="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      padding: "4px 10px",
                      borderRadius: "6px",
                      border: `1px solid ${mnt.divider}`,
                      background: mnt.cardBg,
                      color: safePage >= totalPages ? mnt.textMuted : mnt.ink,
                      cursor: safePage >= totalPages ? "default" : "pointer",
                    }}
                  >
                    Next
                  </Box>
                </Flex>
              )}
            </Flex>
            </>
          )}
        </Box>
      )}

      {matchTarget && (
        <MatchCandidatesModal
          workOrder={matchTarget}
          onClose={() => setMatchTarget(null)}
          onMatched={() => {
            setMatchTarget(null);
            reload();
            if (onChanged) onChanged();
          }}
        />
      )}
    </>
  );
}

/**
 * The SCOPE of a work order, for a queue row.
 *
 * A Corrigo work order's `problemDescription` is the Problem block
 * followed by a blank line and then the whole Note block: property
 * code, location name, FM name/phone/email, quoting instructions,
 * invoicing terms. Several hundred characters, almost all of it
 * reference detail rather than scope.
 *
 * The Problem block is what a row needs — trade category, service, and
 * the contract line item. It is everything before the first blank
 * line. Hand-entered work orders have no such structure, so they fall
 * back to the first line, which is the closest thing to a summary they
 * have. The full text stays available on hover and on the WO detail
 * page; nothing is lost, it just stops dominating the table.
 */
function scopeSummary(text: string | null | undefined): string {
  if (!text) return "";
  const firstBlock = text.split(/\n\s*\n/)[0] || "";
  const lines = firstBlock
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return "";
  // Cap at three: a Corrigo Problem block is exactly three lines, and
  // anything longer is free text that should not spill into a row.
  return lines.slice(0, 3).join(" · ");
}

/**
 * Client-side search over the plan's loaded work orders. Matches the
 * fields an operator would type when hunting a specific case: the Corrigo
 * WO number, the site (WO name, LCP name, location name), the address
 * (line1/city/state/zip), the problem text, the priority label, the NTE.
 *
 * `address` comes off the location as a JSON object (and occasionally as
 * a raw string, depending on how the read parses), so it is flattened
 * defensively rather than assumed - the same shape trap the WO import
 * modal hit with [object Object].
 */
function matchesSearch(wo: any, q: string): boolean {
  const parts: any[] = [
    wo?.externalId,
    wo?.name,
    wo?.problemDescription,
    wo?.priority,
    wo?.nteAmount,
    wo?.lcp?.name,
    wo?.lcp?.location?.name,
  ];

  const addr = wo?.lcp?.location?.address;
  if (typeof addr === "string") {
    parts.push(addr);
  } else if (addr && typeof addr === "object") {
    parts.push(addr.line1, addr.line2, addr.city, addr.state, addr.postalCode, addr.zip);
  }

  for (const p of parts) {
    if (p == null) continue;
    if (String(p).toLowerCase().includes(q)) return true;
  }
  return false;
}

/**
 * Client-side mirror of `WorkOrderService.list`'s lifecycle buckets.
 * The plan fetches its work orders once, unfiltered, so the narrowing
 * happens here. Kept deliberately literal against the server's rules:
 *   open          not terminal (only legacy `rejected` is terminal),
 *                 minus the FINISHED derived stages the server cannot
 *                 express as a status filter
 *   actionable    pre-RFQ (received / matched)
 *   needs_match   pre-RFQ and unmatched
 *   needs_convert pre-RFQ and matched
 *   in_progress   RFQ generated (status 'converted')
 *   terminal      legacy `rejected`
 *   ""            everything
 */
function matchesLifecycle(wo: any, lifecycle: string): boolean {
  const status = wo?.status;
  const PRE_RFQ = status === "received" || status === "matched";
  // N4 fix: "open" now means NOT case-closed, not "stage isn't invoiced".
  // A case is closed only when every money leg has settled (Invoice = Paid
  // AND, when a PO exists, PO Billed/Closed) - the backend `caseClosed`
  // derivation. An invoiced WO with an open project, unsent PO, or unpaid
  // invoice has caseClosed=false and correctly stays OPEN. `caseClosed` is
  // computed on the plan-scoped list (POs attached server-side before
  // computeStage); if it's ever absent, default to open so live work is
  // never hidden. Legacy `rejected` remains terminal regardless.
  const caseClosed = wo?.caseClosed === true;

  switch (lifecycle) {
    case "open":
      return status !== "rejected" && !caseClosed;
    case "actionable":
      return PRE_RFQ;
    case "needs_match":
      return PRE_RFQ && wo?.matchStatus === "unmatched";
    case "needs_convert":
      return (
        PRE_RFQ &&
        ["matched", "manual_matched", "manual_created"].includes(wo?.matchStatus)
      );
    case "in_progress":
      return status === "converted";
    case "terminal":
      return status === "rejected";
    default:
      return true;
  }
}

/**
 * P-b status rollup. Answers "what is waiting on me" before any row is
 * read: how many need a match, how many need an RFQ, how many are out
 * for quote, how much is in production, plus the two aging signals that
 * decide what you touch first (oldest open item, and how many have blown
 * their SLA).
 *
 * Every count is a filter: clicking a chip narrows the list below to
 * exactly the rows it counted. The active chip reads as selected so the
 * strip doubles as the filter state.
 *
 * Counts are computed from the UNFILTERED plan set. Computing them from
 * the visible slice would make them change as you filter by them, which
 * is the failure mode that makes rollups untrustworthy.
 */
function WorkQueueRollup({
  workOrders,
  intakeStyle,
  activeLifecycle,
  activeStage,
  onPickLifecycle,
  onPickStage,
}: {
  workOrders: any[];
  intakeStyle?: "work_order_fed" | "quote_fed";
  activeLifecycle: string;
  activeStage: string;
  onPickLifecycle: (v: string) => void;
  onPickStage: (v: string) => void;
}) {
  const woFed = intakeStyle === "work_order_fed";

  const roll = React.useMemo(() => {
    // Bucket by NEXT STEP. At 125 sites the question is never "how many
    // are at stage X" — it is "what are the piles of work in front of me,
    // and can I do one pile at a time". Each bucket is a batch of
    // identical operator work.
    const byKind: Record<string, number> = {};
    let pastSla = 0;
    let oldestDays: number | null = null;

    const now = moment();
    for (const wo of workOrders) {
      if (wo?.status === "rejected") continue;

      const kind = resolveWoNextAction(wo, { intakeStyle }).kind;
      byKind[kind] = (byKind[kind] ?? 0) + 1;

      const open = wo?.status === "received" || wo?.status === "matched";
      if (open && wo?.receivedAt) {
        // Aging only counts pre-RFQ work: once the RFQ exists the case
        // is moving and its age is the RFQ's problem, not the queue's.
        const days = now.diff(moment(wo.receivedAt), "days");
        if (oldestDays == null || days > oldestDays) oldestDays = days;
      }

      // N4: a case is finished only when case-closed (every money leg
      // settled), not merely when the derived stage is invoiced. An
      // invoiced-but-open WO past its complete-by is still past SLA.
      const finished = wo?.caseClosed === true;
      if (!finished && wo?.completeByAt && moment(wo.completeByAt).isBefore(now, "day")) {
        pastSla++;
      }
    }

    return { byKind, pastSla, oldestDays };
  }, [workOrders, intakeStyle]);

  /**
   * The ladder, in the order work actually flows. Corrigo-fed and
   * quote-fed differ in two rungs: how a quote reaches the client, and
   * whether acceptance is something WE record. Buckets with a zero count
   * are dropped entirely — an empty strip means nothing is waiting, and
   * a row of zeroes is noise at this density.
   */
  const LADDER: Array<{
    kind: string;
    label: string;
    bg: string;
    fg: string;
    lifecycle?: string;
  }> = [
    // Session 80 — FIRST in the ladder, because it is not a rung on it.
    // An outstanding cancellation request decides whether the rest of
    // the work should happen at all, so it reads before "to match".
    // amberDeep rather than amber to separate it from the two amber
    // work piles beside it; a new hue is not an option (the token rules
    // keep status colours to pills and text, and inventing one would
    // assert a priority level the rest of the app does not have).
    {
      kind: "cancellation_requested",
      label: "cancellation requested",
      bg: mnt.amberPill,
      fg: mnt.amberDeep,
    },
    {
      kind: "match",
      label: "to match",
      bg: mnt.redPill,
      fg: mnt.red,
      lifecycle: "needs_match",
    },
    {
      kind: "generate_rfq",
      label: "to price",
      bg: mnt.amberPill,
      fg: mnt.amber,
      lifecycle: "needs_convert",
    },
    { kind: "generate_quote", label: "to quote", bg: mnt.amberPill, fg: mnt.amber },
    woFed
      ? {
          kind: "post_to_corrigo",
          label: "to post to Corrigo",
          bg: mnt.corrigoPill,
          fg: mnt.corrigoPillFg,
        }
      : { kind: "send_quote", label: "to send", bg: mnt.corrigoPill, fg: mnt.corrigoPillFg },
    woFed
      ? {
          kind: "record_acceptance",
          label: "awaiting Corrigo approval",
          bg: mnt.greyPill,
          fg: mnt.grey,
        }
      : { kind: "awaiting_client", label: "awaiting client", bg: mnt.greyPill, fg: mnt.grey },
    // Deposit outstanding is its own pile on purpose. It looks like
    // "awaiting client" and is not: there is an accepted price, a
    // deadline, and money that has not arrived. Amber, not grey.
    {
      kind: "awaiting_deposit",
      label: "awaiting deposit",
      bg: mnt.amberPill,
      fg: mnt.amber,
    },
    { kind: "schedule_project", label: "to schedule", bg: mnt.greenPill, fg: mnt.green },
    { kind: "track_project", label: "in production", bg: mnt.greenPill, fg: mnt.green },
    woFed
      ? {
          kind: "submit_invoice_corrigo",
          label: "to submit to Corrigo",
          bg: mnt.corrigoPill,
          fg: mnt.corrigoPillFg,
        }
      : { kind: "send_invoice", label: "invoiced", bg: mnt.greyPill, fg: mnt.grey },
  ];

  const chips = LADDER.filter((c) => (roll.byKind[c.kind] ?? 0) > 0);

  return (
    <Flex
      sx={{
        background: mnt.bgBeige,
        borderRadius: "6px",
        padding: "8px 12px",
        marginBottom: "10px",
        alignItems: "center",
        gap: "8px",
        flexWrap: "wrap",
      }}
    >
      {chips.length === 0 && (
        <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
          Nothing waiting on this plan.
        </Subtext>
      )}
      {chips.map((c) => {
        const isActive = activeStage === c.kind;
        return (
          <Box
            key={c.kind}
            as="button"
            onClick={() => {
              // The two pre-RFQ buckets have real server-side lifecycle
              // filters; the rest narrow client-side by next step.
              if (c.lifecycle && !isActive) onPickLifecycle(c.lifecycle);
              onPickStage(c.kind);
            }}
            title={"Show the " + (roll.byKind[c.kind] ?? 0) + " " + c.label}
            sx={{
              background: c.bg,
              border: "1px solid",
              borderColor: isActive ? c.fg : "transparent",
              borderRadius: "9px",
              padding: "3px 10px",
              fontFamily: "Poppins",
              fontSize: "10.5px",
              fontWeight: 500,
              color: c.fg,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {roll.byKind[c.kind]} {c.label}
          </Box>
        );
      })}

      {roll.oldestDays != null && (
        <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
          {"oldest " + roll.oldestDays + "d"}
        </Subtext>
      )}
      {roll.pastSla > 0 && (
        <Span fontFamily={"Poppins"} fontSize={"11px"} sx={{ color: mnt.red, fontWeight: 600 }}>
          {roll.pastSla} overdue
        </Span>
      )}

      {(activeLifecycle !== "" || activeStage !== "") && (
        <Box
          as="button"
          onClick={() => {
            onPickStage("");
            onPickLifecycle("");
          }}
          sx={{
            marginLeft: "auto",
            background: "transparent",
            border: "none",
            padding: 0,
            fontFamily: "Poppins",
            fontSize: "11px",
            color: mnt.link,
            cursor: "pointer",
          }}
        >
          Show all
        </Box>
      )}
    </Flex>
  );
}

/**
 * THE PLAN'S WORK-ORDER ROW, in two densities (Session 82).
 *
 * Same design as the location roster: null selection gives the list the
 * full width and its rows earn extra columns; an open record collapses
 * the list and the row sheds them. One component, a `dense` prop.
 *
 * THE NUMBER LEADS, not the site. A work order IS its number — that is
 * what you say to Corrigo, to the client and to the provider. The site
 * is context underneath. A client request has no Corrigo id, so its own
 * name leads instead; "(no ID)" as the loudest thing on the row was
 * exactly backwards.
 *
 * The description is NOT in the row. It averages 113 characters on this
 * plan and 42 of 52 run past 70, so any single-line treatment ellipsises
 * four rows in five — and the description is the job. It lives in the
 * pane, where there is room to read it.
 */
function PlanWoListRow({
  wo,
  isLast,
  dense,
  isOpen,
  onOpen,
}: {
  wo: any;
  isLast: boolean;
  dense: boolean;
  isOpen: boolean;
  onOpen: () => void;
}) {
  const lifecycle = lifecycleBadge(wo);
  const address = wo.lcp?.location?.address;
  const city = address ? [address.city, address.state].filter(Boolean).join(", ") : "";
  const siteName = wo.lcp?.name || wo.lcp?.location?.name || address?.line1 || "Unmatched site";
  const label = wo.externalId || wo.name || "Client request";
  const received = wo.receivedAt ? moment(wo.receivedAt).format("MMM D") : "";

  // The dot carries the stage at a glance so the row does not need a
  // pill. Twelve pills down a column is a wall nobody reads.
  const tone = lifecycle?.variant || "";
  const dotColor = /overdue|danger|error/i.test(tone)
    ? mnt.red
    : /warning|due|amber/i.test(tone)
    ? mnt.amber
    : /success|complete|closed/i.test(tone)
    ? mnt.green
    : /info|progress|primary/i.test(tone)
    ? mnt.link
    : mnt.amber;

  return (
    <Flex
      onClick={onOpen}
      sx={{
        alignItems: "center",
        gap: dense ? "10px" : "12px",
        padding: dense ? "10px 14px" : "10px 16px",
        borderBottom: isLast ? "none" : "0.5px solid",
        borderColor: mnt.divider,
        background: isOpen ? mnt.bgBeige : "transparent",
        cursor: "pointer",
        "&:hover": { background: isOpen ? mnt.bgBeige : mnt.bgSubtle },
      }}
    >
      <Box
        sx={{ width: "7px", height: "7px", borderRadius: "4px", background: dotColor, flexShrink: 0 }}
      />

      <Box sx={{ flex: dense ? "1 1 auto" : "0 0 190px", minWidth: 0 }}>
        <Box
          as="div"
          sx={{
            fontFamily: "Poppins",
            fontSize: "12.5px",
            fontWeight: 500,
            color: mnt.link,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Box>
        {dense && (
          <Box
            as="div"
            sx={{
              fontFamily: "Poppins",
              fontSize: "10.5px",
              color: mnt.textFaint,
              marginTop: "2px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {[siteName, city].filter(Boolean).join(" \u00b7 ")}
          </Box>
        )}
      </Box>

      {!dense && (
        <>
          <Box
            as="div"
            sx={{
              fontFamily: "Poppins",
              fontSize: "12px",
              flex: "1 1 auto",
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {[siteName, city].filter(Boolean).join(" \u00b7 ")}
          </Box>
          <Box
            as="div"
            sx={{
              fontFamily: "Poppins",
              fontSize: "11.5px",
              color: mnt.textMuted,
              width: "120px",
              flexShrink: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {lifecycle?.label || ""}
          </Box>
        </>
      )}

      <Box
        as="div"
        sx={{
          fontFamily: "Poppins",
          fontSize: "11px",
          color: mnt.textMuted,
          width: dense ? "46px" : "54px",
          textAlign: "right",
          flexShrink: 0,
        }}
      >
        {received}
      </Box>
    </Flex>
  );
}

/**
 * THE OPEN WORK ORDER, beside the list.
 *
 * Everything the row stopped carrying: the full description (not the
 * two-line scope summary — here it can be read), the SLA, the NTE, and
 * the actions. The case file stays one click away for the full record.
 */
function PlanWoPane({
  wo,
  canEdit,
  onClose,
  onOpen,
  onMatch,
}: {
  wo: any;
  canEdit: boolean;
  onClose: () => void;
  onOpen: () => void;
  onMatch: () => void;
}) {
  const lifecycle = lifecycleBadge(wo);
  const sla = slaCell(wo);
  const address = wo.lcp?.location?.address;
  const city = address ? [address.city, address.state].filter(Boolean).join(", ") : "";
  const siteName = wo.lcp?.name || wo.lcp?.location?.name || address?.line1 || "Unmatched site";
  const label = wo.externalId || wo.name || "Client request";
  const canMatch = canEdit && canMatchWo(wo);
  const canGenerate = canEdit && canGenerateRfqForWo(wo);

  const secondary = {
    fontFamily: "Poppins",
    fontSize: "12px",
    fontWeight: 500,
    padding: "8px 14px",
    borderRadius: "6px",
    background: mnt.cardBg,
    border: `1px solid ${mnt.divider}`,
    color: mnt.ink,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
  };

  return (
    <Box
      sx={{
        background: mnt.cardBg,
        border: "0.5px solid",
        borderColor: mnt.divider,
        borderRadius: "8px",
        flex: "1 1 auto",
        minWidth: 0,
        padding: "20px 22px",
      }}
    >
      <Flex sx={{ alignItems: "flex-start", gap: "14px", marginBottom: "16px" }}>
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <Box as="div" sx={{ fontFamily: "Poppins", fontSize: "17px", fontWeight: 600 }}>
            {label}
          </Box>
          <Box
            as="div"
            sx={{ fontFamily: "Poppins", fontSize: "11.5px", color: mnt.textMuted, marginTop: "4px" }}
          >
            {[siteName, city, wo.receivedAt ? "received " + moment(wo.receivedAt).format("MMM D") : null]
              .filter(Boolean)
              .join(" \u00b7 ")}
          </Box>
        </Box>
        {lifecycle && (
          <Badge as="span" variant={lifecycle.variant as any}>
            {lifecycle.label}
          </Badge>
        )}
        <button type="button" onClick={onClose} style={{ ...secondary, padding: "6px 10px" }}>
          Close
        </button>
      </Flex>

      <Flex sx={{ gap: "8px", flexWrap: "wrap", marginBottom: "18px" }}>
        {canGenerate && (
          <button
            type="button"
            onClick={onOpen}
            style={{
              fontFamily: "Poppins",
              fontSize: "12px",
              fontWeight: 500,
              padding: "8px 14px",
              borderRadius: "6px",
              background: mnt.ink,
              border: "none",
              color: mnt.cardBg,
              cursor: "pointer",
            }}
          >
            Generate RFQ
          </button>
        )}
        {canMatch && (
          <button type="button" onClick={onMatch} style={secondary}>
            Match to a site
          </button>
        )}
        <button type="button" onClick={onOpen} style={secondary}>
          Open work order
        </button>
      </Flex>

      {wo.problemDescription && (
        <Box sx={{ marginBottom: "18px" }}>
          <Box sx={{ ...mntType.labelSm, fontWeight: 600, letterSpacing: "0.3px", marginBottom: "7px" }}>
            WHAT WAS ASKED FOR
          </Box>
          {/* The WHOLE field, not `scopeSummary`. The row could not show
              this; the pane exists so it can. */}
          <Box
            as="div"
            sx={{
              fontFamily: "Poppins",
              fontSize: "12.5px",
              lineHeight: "19px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {wo.problemDescription}
          </Box>
        </Box>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "10px" }}>
        <PlanWoStat
          label="COMPLETE BY"
          value={sla?.label || "Not set"}
          sub={wo.priority === "urgent" ? "Urgent" : ""}
        />
        <PlanWoStat
          label="NTE"
          value={wo.nteAmount != null && wo.nteAmount !== "" ? formatNte(wo.nteAmount) : "None"}
          sub={wo.nteAmount != null && wo.nteAmount !== "" ? "Ceiling, not a price" : ""}
        />
        <PlanWoStat
          label="SERVICE"
          value={wo.serviceType?.name || wo.service?.name || "Not set"}
          sub={wo.quantity ? String(wo.quantity) : ""}
        />
      </Box>
    </Box>
  );
}

function PlanWoStat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <Box sx={{ background: mnt.bgSubtle, borderRadius: "8px", padding: "13px 15px" }}>
      <Box sx={{ ...mntType.labelSm, fontWeight: 600, letterSpacing: "0.3px" }}>{label}</Box>
      <Box as="div" sx={{ fontFamily: "Poppins", fontSize: "17px", fontWeight: 600, marginTop: "5px" }}>
        {value}
      </Box>
      {sub ? (
        <Box as="div" sx={{ fontFamily: "Poppins", fontSize: "11px", color: mnt.textMuted, marginTop: "2px" }}>
          {sub}
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * The wide-state column header. Rendered only when no record is open,
 * because narrow there are no columns left to label. Widths mirror
 * `PlanWoListRow`'s wide branch exactly — they are the same layout and
 * drift between them shows up immediately as misaligned columns.
 */
function PlanWoListHeader() {
  const cell = {
    ...mntType.labelSm,
    fontWeight: 600,
    letterSpacing: "0.3px",
  };
  return (
    <Flex
      sx={{
        padding: "9px 16px",
        borderBottom: "0.5px solid",
        borderColor: mnt.divider,
        background: mnt.bgSubtle,
        gap: "12px",
        alignItems: "center",
      }}
    >
      {/* Matches the row's status dot so the columns line up. */}
      <Box sx={{ width: "7px", flexShrink: 0 }} />
      <Box sx={{ ...cell, flex: "0 0 190px" }}>WORK ORDER</Box>
      <Box sx={{ ...cell, flex: "1 1 auto", minWidth: 0 }}>SITE</Box>
      <Box sx={{ ...cell, width: "120px", flexShrink: 0 }}>STAGE</Box>
      <Box sx={{ ...cell, width: "54px", flexShrink: 0, textAlign: "right" }}>RECEIVED</Box>
    </Flex>
  );
}

function PlanWoHeaderRow() {
  const cols = [
    { flex: "0 0 14%", label: "WO #" },
    { flex: "0 0 8%", label: "SOURCE" },
    { flex: "0 0 22%", label: "SITE" },
    { flex: "0 0 20%", label: "SERVICE · DUE · NTE" },
    { flex: "0 0 6%", label: "AGE" },
    { flex: "0 0 12%", label: "STAGE" },
    { flex: 1, label: "" },
  ];
  return (
    <Flex
      sx={{
        padding: "10px 16px",
        borderBottom: "0.5px solid",
        borderColor: mnt.divider,
        background: mnt.bgSubtle,
        gap: "10px",
        alignItems: "center",
      }}
    >
      {cols.map((c, i) => (
        <Box key={i} sx={{ flex: c.flex }}>
          <Box sx={{ ...mntType.labelSm, fontWeight: 600, letterSpacing: "0.3px" }}>{c.label}</Box>
        </Box>
      ))}
    </Flex>
  );
}

function PlanWoRow({
  wo,
  isLast,
  canEdit,
  intakeStyle,
  onOpen,
  onMatch,
  onGenerateRfq,
  onNavigate,
}: {
  wo: any;
  isLast: boolean;
  canEdit: boolean;
  /** Decides the quote step: paste to Corrigo vs email the client. */
  intakeStyle?: "work_order_fed" | "quote_fed";
  onOpen: () => void;
  onMatch: () => void;
  onGenerateRfq: () => void;
  /** Navigate to where the next step is performed. */
  onNavigate: (path: string) => void;
}) {
  const canMatch = canEdit && canMatchWo(wo);
  const canGenerate = canEdit && canGenerateRfqForWo(wo);

  const address = wo.lcp?.location?.address;
  const city = address ? [address.city, address.state].filter(Boolean).join(", ") : "";
  const siteName = wo.lcp?.name || wo.lcp?.location?.name || address?.line1 || null;

  // Plan-scoped: "site not in any plan" cannot happen here by
  // construction, so only the no-LCP case is a real cue.
  const reason = woUnresolvedReason(wo, false);

  const lifecycle = lifecycleBadge(wo);
  const sla = slaCell(wo);
  const received = receivedCell(wo);

  return (
    <Flex
      sx={{
        alignItems: "flex-start",
        gap: "10px",
        padding: "12px 16px",
        borderBottom: isLast ? "none" : "0.5px solid",
        borderColor: mnt.divider,
        "&:hover": { background: mnt.bgSubtle },
      }}
    >
      {/* WO # */}
      <Box sx={{ flex: "0 0 14%", minWidth: 0 }}>
        <button
          type="button"
          onClick={onOpen}
          title="Open work order"
          style={{
            display: "block",
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            background: "transparent",
            border: "none",
            padding: 0,
            textAlign: "left",
            cursor: "pointer",
            fontFamily: "Poppins",
            fontWeight: 600,
            fontSize: "13px",
            color: themevals.colors.primary,
            textDecoration: "underline",
          }}
        >
          {wo.externalId || wo.name || "(no ID)"}
        </button>
      </Box>

      {/* Source */}
      <Box sx={{ flex: "0 0 8%", minWidth: 0 }}>
        <SourcePill sourceType={wo.sourceType} />
        {/* Map status. Shown at every stage except rejected — the pill
         * itself changes register past the RFQ (amber prompt -> grey
         * fact), so suppressing the whole thing there only hid the map
         * state of every manually created WO. See showsMapStatus. */}
        {showsMapStatus(wo) && (
          <Box sx={{ marginTop: "4px" }}>
            <MapStatusPill wo={wo} />
          </Box>
        )}
      </Box>

      {/* Site */}
      <Box sx={{ flex: "0 0 22%", minWidth: 0 }}>
        {siteName ? (
          <>
            <Span
              fontFamily={"Poppins"}
              fontSize={"12px"}
              fontWeight={600}
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {siteName}
            </Span>
            {city && (
              <Subtext
                fontSize={"11px"}
                fontFamily={"Poppins"}
                margin={0}
                color={mnt.textMuted}
                sx={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {city}
              </Subtext>
            )}
          </>
        ) : (
          <UnresolvedPill reason={reason || "No matching site"} />
        )}
      </Box>

      {/* Service · SLA · NTE. NTE folded in here (mockup 29) so the
          action column stops running past the card edge.

          SCOPE, NOT THE WHOLE FIELD. `problemDescription` on a Corrigo
          WO is the Problem block followed by the entire Note block —
          location notes, FM contact, quoting instructions, several
          hundred characters. Rendering it raw turned every row into a
          paragraph and made this table unreadable at 125 sites. The
          Problem block is the first three lines (trade category,
          service, contract line item); everything after the blank line
          is reference detail that belongs on the WO detail page, not in
          a queue row. */}
      <Box sx={{ flex: "0 0 20%", minWidth: 0 }}>
        <Span
          fontFamily={"Poppins"}
          fontSize={"12px"}
          title={wo.problemDescription || undefined}
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            wordBreak: "break-word",
          }}
        >
          {scopeSummary(wo.problemDescription) || "-"}
        </Span>
        {sla && (
          <Subtext
            fontSize={"11px"}
            fontFamily={"Poppins"}
            margin={0}
            sx={{ color: sla.color, fontWeight: sla.weight }}
          >
            {sla.label}
          </Subtext>
        )}
        {wo.nteAmount != null && wo.nteAmount !== "" && (
          <Subtext
            fontSize={"11px"}
            fontFamily={"Poppins"}
            margin={0}
            color={mnt.textMuted}
          >
            NTE {formatNte(wo.nteAmount)}
          </Subtext>
        )}
        {wo.priority === "urgent" && (
          <Box sx={{ marginTop: "3px" }}>
            <UrgentPill />
          </Box>
        )}
      </Box>

      {/* Age — how long this has been sitting. The column the queue was
          missing: at 23 open WOs, age is what decides order of attack. */}
      <Box sx={{ flex: "0 0 6%", minWidth: 0 }}>
        <Span
          fontFamily={"Poppins"}
          fontSize={"12px"}
          sx={{ color: received.color, fontWeight: received.weight }}
        >
          {received.label.replace(" ago", "")}
        </Span>
      </Box>

      {/* Stage */}
      <Box sx={{ flex: "0 0 12%", minWidth: 0 }}>
        <Badge as="span" variant={lifecycle.variant as any}>
          {lifecycle.label}
        </Badge>
      </Box>

      {/* Actions. Same weighting as the queue: filled for the ready
          action, outlined for the one needing a decision first. */}
      <Flex
        sx={{
          flex: "0 0 auto",
          justifyContent: "flex-end",
          gap: "6px",
          flexWrap: "nowrap",
          whiteSpace: "nowrap",
        }}
      >
        {(() => {
          /**
           * The row states the case's NEXT STEP and takes you where it is
           * done. At 125 sites the plan list is the work queue: a row that
           * only links to a record forces the operator to open every case
           * to find out what it needs. Corrigo vs quote-fed changes the
           * step (paste vs send, record-acceptance vs wait), so the plan's
           * intake style is passed in. Shared with the case file's guided
           * card via `workOrderNextAction` so the two cannot disagree.
           */
          const next = resolveWoNextAction(wo, { intakeStyle });
          if (!next.path) return null;
          // Match and Generate RFQ have their own dedicated buttons
          // below (they open a modal / are the filled CTA already), so
          // don't render them twice.
          if (next.kind === "match" || next.kind === "generate_rfq") return null;
          return (
            <Box
              title={next.hint}
              sx={{ display: "inline-flex", flexShrink: 0 }}
            >
              <BasicButton
                type="button"
                outline={!next.primary}
                scheme={next.primary ? "primary" : "secondary"}
                styles={{ whiteSpace: "nowrap", flexShrink: "0", margin: "0" }}
                onClick={() => onNavigate(next.path as string)}
              >
                {next.label}
              </BasicButton>
            </Box>
          );
        })()}
        {canMatch && (
          <BasicButton
            type="button"
            outline
            scheme="primary"
            styles={{ whiteSpace: "nowrap", flexShrink: "0" }}
            onClick={onMatch}
          >
            Match
          </BasicButton>
        )}
        {canGenerate && (
          <BasicButton
            type="button"
            scheme="primary"
            styles={{ whiteSpace: "nowrap", flexShrink: "0" }}
            onClick={onGenerateRfq}
          >
            Generate RFQ
          </BasicButton>
        )}
      </Flex>
    </Flex>
  );
}
